import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Eye, Upload, Palette, Wand2, ArrowLeft, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CertificateDesigner } from './CertificateDesigner';

interface CertificateTemplate {
  id: string;
  name: string;
  description: string;
  template_data: any;
  background_type?: string;
  background_value?: string;
  logo_url?: string;
  is_active: boolean;
  created_at: string;
}

export function CertificateTemplateManager() {
  const [templates, setTemplates] = useState<CertificateTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<CertificateTemplate | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [designerMode, setDesignerMode] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<CertificateTemplate | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const SAMPLE_TEMPLATES = [
    {
      id: 'sample-1',
      name: 'Professional Blue Certificate',
      description: 'Clean professional certificate with blue accents and elegant typography',
      template_data: {
        title: "Certificate of Professional Achievement",
        subtitle: "Professional Development Program",
        layout: "centered",
        fonts: {
          title: { family: "serif", size: 32, weight: "bold", color: "#1e40af" },
          subtitle: { family: "sans-serif", size: 18, weight: "normal", color: "#6b7280" },
          participant: { family: "serif", size: 28, weight: "bold", color: "#111827" },
          course: { family: "sans-serif", size: 20, weight: "normal", color: "#374151" },
          details: { family: "sans-serif", size: 14, weight: "normal", color: "#6b7280" }
        },
        border: { enabled: true, color: "#1e40af", width: 4, style: "solid", cornerRadius: 12 },
        watermark: { enabled: false, image: "", opacity: 0.1, position: "center" },
        logo: { enabled: true, url: "", position: "top-left", size: 80 },
        signatures: {
          enabled: true,
          instructor: { enabled: true, label: "Course Instructor", position: "bottom-left" },
          admin: { enabled: true, label: "Program Director", position: "bottom-right" }
        },
        dates: {
          completion: { enabled: true, label: "Date of Completion" },
          issued: { enabled: true, label: "Certificate Issued" }
        },
        pdHours: { enabled: true, label: "Professional Development Hours", position: "bottom-center" },
        spacing: { padding: 40, sections: 20 }
      },
      background_type: 'gradient',
      background_value: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      is_active: true,
      created_at: new Date().toISOString()
    },
    {
      id: 'sample-2',
      name: 'Elegant Gold Certificate',
      description: 'Formal certificate with gold accents and decorative elements',
      template_data: {
        title: "Certificate of Excellence",
        subtitle: "In Recognition of Outstanding Achievement",
        layout: "centered",
        fonts: {
          title: { family: "serif", size: 30, weight: "bold", color: "#d97706" },
          subtitle: { family: "serif", size: 16, weight: "italic", color: "#92400e" },
          participant: { family: "serif", size: 26, weight: "bold", color: "#111827" },
          course: { family: "serif", size: 18, weight: "normal", color: "#374151" },
          details: { family: "serif", size: 12, weight: "normal", color: "#6b7280" }
        },
        border: { enabled: true, color: "#d97706", width: 6, style: "double", cornerRadius: 8 },
        watermark: { enabled: true, image: "", opacity: 0.08, position: "center" },
        logo: { enabled: true, url: "", position: "top-center", size: 100 },
        signatures: {
          enabled: true,
          instructor: { enabled: true, label: "Lead Instructor", position: "bottom-left" },
          admin: { enabled: true, label: "Academic Director", position: "bottom-right" }
        },
        dates: {
          completion: { enabled: true, label: "Completed On" },
          issued: { enabled: true, label: "Issued On" }
        },
        pdHours: { enabled: true, label: "Contact Hours", position: "bottom-center" },
        spacing: { padding: 50, sections: 25 }
      },
      background_type: 'gradient',
      background_value: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
      is_active: true,
      created_at: new Date().toISOString()
    },
    {
      id: 'sample-3',
      name: 'Modern Green Certificate',
      description: 'Contemporary design with green accents and clean lines',
      template_data: {
        title: "Professional Development Certificate",
        subtitle: "Continuing Education Achievement",
        layout: "centered",
        fonts: {
          title: { family: "sans-serif", size: 28, weight: "bold", color: "#059669" },
          subtitle: { family: "sans-serif", size: 16, weight: "normal", color: "#047857" },
          participant: { family: "sans-serif", size: 24, weight: "bold", color: "#111827" },
          course: { family: "sans-serif", size: 18, weight: "normal", color: "#374151" },
          details: { family: "sans-serif", size: 13, weight: "normal", color: "#6b7280" }
        },
        border: { enabled: true, color: "#059669", width: 3, style: "solid", cornerRadius: 16 },
        watermark: { enabled: false, image: "", opacity: 0.1, position: "center" },
        logo: { enabled: true, url: "", position: "top-right", size: 90 },
        signatures: {
          enabled: true,
          instructor: { enabled: true, label: "Facilitator", position: "bottom-left" },
          admin: { enabled: true, label: "Department Head", position: "bottom-right" }
        },
        dates: {
          completion: { enabled: true, label: "Course Completed" },
          issued: { enabled: true, label: "Certificate Date" }
        },
        pdHours: { enabled: true, label: "Training Hours", position: "bottom-center" },
        spacing: { padding: 35, sections: 18 }
      },
      background_type: 'solid',
      background_value: '#ffffff',
      is_active: true,
      created_at: new Date().toISOString()
    }
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('certificate_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Combine sample templates with database templates
      const allTemplates = [...SAMPLE_TEMPLATES, ...(data || [])];
      setTemplates(allTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      // If database fails, show sample templates
      setTemplates(SAMPLE_TEMPLATES);
      toast.error('Using sample templates - database connection failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    const newTemplate: CertificateTemplate = {
      id: '',
      name: 'New Certificate Template',
      description: 'Custom certificate template',
      template_data: SAMPLE_TEMPLATES[0].template_data, // Use first sample as base
      background_type: 'solid',
      background_value: '#ffffff',
      is_active: true,
      created_at: ''
    };
    setEditingTemplate(newTemplate);
    setDesignerMode(true);
  };

  const handleEditTemplate = (template: CertificateTemplate) => {
    setEditingTemplate(template);
    setDesignerMode(true);
  };

  const handleUseSample = (sampleTemplate: any) => {
    const newTemplate: CertificateTemplate = {
      id: '',
      name: `${sampleTemplate.name} (Copy)`,
      description: sampleTemplate.description,
      template_data: sampleTemplate.template_data,
      background_type: sampleTemplate.background_type,
      background_value: sampleTemplate.background_value,
      is_active: true,
      created_at: ''
    };
    setEditingTemplate(newTemplate);
    setDesignerMode(true);
  };

  const handlePreviewTemplate = (template: CertificateTemplate) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const generatePreview = (templateData: any, backgroundType: string, backgroundValue: string) => {
    return (
      <div 
        className="relative w-full aspect-[8.5/11] mx-auto bg-white shadow-lg overflow-hidden animate-fade-in"
        style={{
          background: backgroundType === 'solid' ? backgroundValue : 
                     backgroundType === 'gradient' ? backgroundValue : 
                     `url(${backgroundValue}) center/cover`,
          border: templateData.border?.enabled ? 
            `${templateData.border.width}px ${templateData.border.style} ${templateData.border.color}` : 'none',
          borderRadius: templateData.border?.cornerRadius ? `${templateData.border.cornerRadius}px` : '0'
        }}
      >
        {/* Watermark */}
        {templateData.watermark?.enabled && templateData.watermark?.image && (
          <div 
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ opacity: templateData.watermark.opacity }}
          >
            <img 
              src={templateData.watermark.image} 
              alt="Watermark" 
              className="max-w-full max-h-full object-contain"
            />
          </div>
        )}

        {/* Logo */}
        {templateData.logo?.enabled && templateData.logo?.url && (
          <div 
            className={`absolute ${
              templateData.logo.position === 'top-left' ? 'top-4 left-4' :
              templateData.logo.position === 'top-right' ? 'top-4 right-4' :
              templateData.logo.position === 'top-center' ? 'top-4 left-1/2 transform -translate-x-1/2' :
              'top-4 left-4'
            }`}
          >
            <div 
              className="bg-gray-200 rounded flex items-center justify-center text-gray-500 text-xs"
              style={{ width: `${templateData.logo.size}px`, height: `${templateData.logo.size}px` }}
            >
              LOGO
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex flex-col items-center justify-center h-full p-8 text-center space-y-4">
          <h1 
            className="font-serif"
            style={{
              fontSize: `${templateData.fonts?.title?.size || 32}px`,
              fontWeight: templateData.fonts?.title?.weight || 'bold',
              color: templateData.fonts?.title?.color || '#1e40af'
            }}
          >
            {templateData.title}
          </h1>
          
          <p 
            style={{
              fontSize: `${templateData.fonts?.subtitle?.size || 18}px`,
              color: templateData.fonts?.subtitle?.color || '#6b7280'
            }}
          >
            {templateData.subtitle}
          </p>

          <div className="py-4">
            <p className="text-lg text-gray-600 mb-2">This certifies that</p>
            <p 
              className="font-serif border-b-2 border-gray-300 pb-2 mb-4 min-w-64"
              style={{
                fontSize: `${templateData.fonts?.participant?.size || 28}px`,
                fontWeight: templateData.fonts?.participant?.weight || 'bold',
                color: templateData.fonts?.participant?.color || '#111827'
              }}
            >
              [Participant Name]
            </p>
            <p className="text-lg text-gray-600 mb-2">has successfully completed</p>
            <p 
              className="font-medium"
              style={{
                fontSize: `${templateData.fonts?.course?.size || 20}px`,
                color: templateData.fonts?.course?.color || '#374151'
              }}
            >
              [Course Title]
            </p>
          </div>

          {/* PD Hours */}
          {templateData.pdHours?.enabled && (
            <div className="text-sm text-gray-600">
              <p>{templateData.pdHours.label}: [X] Hours</p>
            </div>
          )}

          {/* Dates */}
          {(templateData.dates?.completion?.enabled || templateData.dates?.issued?.enabled) && (
            <div className="flex gap-8 text-sm text-gray-600">
              {templateData.dates?.completion?.enabled && (
                <div>
                  <p>{templateData.dates.completion.label}:</p>
                  <p className="border-b border-gray-300 min-w-24">__________</p>
                </div>
              )}
              {templateData.dates?.issued?.enabled && (
                <div>
                  <p>{templateData.dates.issued.label}:</p>
                  <p className="border-b border-gray-300 min-w-24">__________</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Signatures */}
        {templateData.signatures?.enabled && (
          <div className="absolute bottom-8 left-8 right-8 flex justify-between">
            {templateData.signatures?.instructor?.enabled && (
              <div className="text-center">
                <div className="border-b border-gray-400 w-48 mb-2"></div>
                <p className="text-sm text-gray-600">{templateData.signatures.instructor.label}</p>
              </div>
            )}
            {templateData.signatures?.admin?.enabled && (
              <div className="text-center">
                <div className="border-b border-gray-400 w-48 mb-2"></div>
                <p className="text-sm text-gray-600">{templateData.signatures.admin.label}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleSaveTemplate = async (template: CertificateTemplate) => {
    try {
      if (template.id && !template.id.startsWith('sample-')) {
        const { error } = await supabase
          .from('certificate_templates')
          .update({
            name: template.name,
            description: template.description,
            template_data: template.template_data,
            background_type: template.background_type,
            background_value: template.background_value,
            is_active: template.is_active
          })
          .eq('id', template.id);

        if (error) throw error;
        toast.success('Template updated successfully');
      } else {
        const { error } = await supabase
          .from('certificate_templates')
          .insert({
            name: template.name,
            description: template.description,
            template_data: template.template_data,
            background_type: template.background_type,
            background_value: template.background_value,
            is_active: template.is_active
          });

        if (error) throw error;
        toast.success('Template created successfully');
      }
      
      setDesignerMode(false);
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (id.startsWith('sample-')) {
      toast.error('Cannot delete sample templates');
      return;
    }

    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const { error } = await supabase
        .from('certificate_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Template deleted successfully');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  if (designerMode && editingTemplate) {
    return (
      <CertificateDesigner
        template={editingTemplate}
        onSave={handleSaveTemplate}
        onCancel={() => {
          setDesignerMode(false);
          setEditingTemplate(null);
        }}
      />
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading templates...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Certificate Templates</h3>
          <p className="text-sm text-muted-foreground">
            Choose from professional templates or create your own custom design
          </p>
        </div>
        <Button onClick={handleCreateTemplate} className="hover-scale">
          <Plus className="h-4 w-4 mr-2" />
          Create Custom Template
        </Button>
      </div>

      {/* Sample Templates Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-primary" />
          <h4 className="text-lg font-medium">Professional Templates</h4>
          <Badge variant="secondary">Ready to Use</Badge>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SAMPLE_TEMPLATES.map((template) => (
            <Card key={template.id} className="relative group hover-scale">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{template.name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {template.description}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    Sample
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => handleUseSample(template)}
                    className="flex-1"
                  >
                    <Wand2 className="h-3 w-3 mr-1" />
                    Use & Customize
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handlePreviewTemplate(template)}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Preview
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Custom Templates Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Edit className="h-5 w-5 text-primary" />
          <h4 className="text-lg font-medium">Your Custom Templates</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.filter(t => !t.id.startsWith('sample-')).map((template) => (
            <Card key={template.id} className="relative hover-scale">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{template.name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {template.description}
                    </p>
                  </div>
                  <Badge variant={template.is_active ? 'default' : 'secondary'}>
                    {template.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleEditTemplate(template)}
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handlePreviewTemplate(template)}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Preview
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleDeleteTemplate(template.id)}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {templates.filter(t => !t.id.startsWith('sample-')).length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="space-y-4">
                <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center">
                  <Plus className="h-8 w-8 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium">No custom templates yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Create your first custom certificate template or customize one of our professional templates above.
                  </p>
                </div>
                <Button onClick={handleCreateTemplate} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Template
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>
                Certificate Preview - {previewTemplate?.name}
              </DialogTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreview(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          {previewTemplate && (
            <div className="p-4">
              <div className="scale-75 origin-top">
                {generatePreview(
                  previewTemplate.template_data,
                  previewTemplate.background_type || 'solid',
                  previewTemplate.background_value || '#ffffff'
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}